// Renderiza a apresentação Palpites Show como um vídeo (WebM/MP4)
// 100% client-side via Canvas + MediaRecorder. Zero créditos de IA.
// Trilhas: procedurais via Web Audio API (royalty-free, geradas ao vivo).

import { PalpitesData } from "./palpitesGenerator";

export type TrilhaId = "nenhuma" | "suspense" | "urbana" | "vitoria";
export type FormatoVideo = "vertical" | "horizontal";
export type SlidesModo = "todos" | "ouro" | "resumido";
export type ModoDuracao = "padrao" | "rapido" | "curto15" | "curto30";

export interface VideoOptions {
  /** legacy: duração total em segundos (usado quando modoDuracao não é fornecido) */
  duracaoSegundos?: 15 | 30;
  /** novo: preset de duração por slide */
  modoDuracao?: ModoDuracao;
  trilha: TrilhaId;
  slides: SlidesModo;
  formato: FormatoVideo;
  onProgress?: (pct: number) => void;
}

export interface SlideDef {
  titulo: string;
  linhas: string[];
  destaque?: string;
}

function buildSlides(data: PalpitesData, modo: SlidesModo): SlideDef[] {
  const capa: SlideDef = {
    titulo: "PALPITE DO IA PALPITE GURU",
    linhas: [data.data],
    destaque: "🐯 🐘 🦁",
  };
  const bicho: SlideDef = {
    titulo: "BICHO DO DIA",
    linhas: data.bichoDoDia,
  };
  const ouro: SlideDef[] = data.palpitesOuro.map((p, i) => ({
    titulo: `PALPITE DE OURO ${i + 1}`,
    linhas: [
      `${p.grupo} • ${p.animal}`,
      `Dezena ${p.dezena}`,
      `Centenas: ${p.centenas.join("  ")}`,
      `Milhares: ${p.milhares.join("  ")}`,
    ],
  }));
  const passe: SlideDef = {
    titulo: "PASSE VAI E VEM",
    linhas: data.passeVaiVem.map((p) => `${p.g1} ↔ ${p.g2}`),
  };
  const duques: SlideDef = {
    titulo: "DUQUES DE DEZENAS",
    linhas: data.duques.map((d) => d.numeros.join(" - ")),
  };
  const ternos: SlideDef = {
    titulo: "TERNOS DE DEZENAS",
    linhas: data.ternos.map((t) => t.numeros.join(" - ")),
  };
  const bonus: SlideDef = {
    titulo: "BÔNUS FORTE",
    linhas: [`Milhar: ${data.bonusMilhar}`, `Centena: ${data.bonusCentena}`],
  };
  const cta: SlideDef = {
    titulo: "IA PALPITE GURU",
    linhas: [
      "Palpites com Inteligência Artificial",
      "Novo GURU CHAT disponível!",
      "aipalpiteguru.com",
    ],
    destaque: "🚀",
  };

  if (modo === "ouro") return [capa, ...ouro, cta];
  if (modo === "resumido") return [capa, bicho, ...ouro, cta];
  return [capa, bicho, ...ouro, passe, duques, ternos, bonus, cta];
}

function computeDurations(slides: SlideDef[], opts: VideoOptions): number[] {
  const modo = opts.modoDuracao;
  if (modo === "padrao") {
    // 20s capa + 40s por slide
    return slides.map((_, i) => (i === 0 ? 20 : 40));
  }
  if (modo === "rapido") {
    // 10s capa + 20s por slide
    return slides.map((_, i) => (i === 0 ? 10 : 20));
  }
  if (modo === "curto15") {
    const per = 15 / slides.length;
    return slides.map(() => per);
  }
  if (modo === "curto30") {
    const per = 30 / slides.length;
    return slides.map(() => per);
  }
  // fallback: uniform da duracaoSegundos
  const total = opts.duracaoSegundos ?? 30;
  const per = total / slides.length;
  return slides.map(() => per);
}

/* ---------- Áudio procedural ---------- */

export function startAudioTrack(ctx: AudioContext, trilha: TrilhaId): {
  destination: MediaStreamAudioDestinationNode;
  stop: () => void;
} {
  const destination = ctx.createMediaStreamDestination();
  const master = ctx.createGain();
  master.gain.value = trilha === "nenhuma" ? 0 : 0.18;
  master.connect(destination);
  master.connect(ctx.destination);

  const stops: Array<() => void> = [];

  if (trilha === "nenhuma") {
    return { destination, stop: () => master.disconnect() };
  }

  const bpm = trilha === "urbana" ? 92 : trilha === "vitoria" ? 128 : 78;
  const beatDur = 60 / bpm;
  let cancelled = false;

  const scheduleLoop = () => {
    if (cancelled) return;
    const now = ctx.currentTime;
    for (let i = 0; i < 8; i++) {
      const t = now + i * beatDur;
      const kick = ctx.createOscillator();
      const kg = ctx.createGain();
      kick.frequency.setValueAtTime(120, t);
      kick.frequency.exponentialRampToValueAtTime(40, t + 0.15);
      kg.gain.setValueAtTime(0.9, t);
      kg.gain.exponentialRampToValueAtTime(0.001, t + 0.18);
      kick.connect(kg).connect(master);
      kick.start(t);
      kick.stop(t + 0.2);

      const noteFreqs =
        trilha === "vitoria"
          ? [523, 659, 784, 1046]
          : trilha === "urbana"
          ? [220, 277, 330, 220]
          : [196, 233, 293, 349];
      const nf = noteFreqs[i % noteFreqs.length];
      const osc = ctx.createOscillator();
      const og = ctx.createGain();
      osc.type = trilha === "suspense" ? "sawtooth" : "triangle";
      osc.frequency.setValueAtTime(nf, t + 0.05);
      og.gain.setValueAtTime(0, t + 0.05);
      og.gain.linearRampToValueAtTime(0.25, t + 0.08);
      og.gain.exponentialRampToValueAtTime(0.001, t + beatDur * 0.9);
      osc.connect(og).connect(master);
      osc.start(t + 0.05);
      osc.stop(t + beatDur);
    }
    setTimeout(scheduleLoop, beatDur * 8 * 1000 - 50);
  };
  scheduleLoop();

  stops.push(() => {
    cancelled = true;
    try {
      master.disconnect();
    } catch {}
  });

  return { destination, stop: () => stops.forEach((s) => s()) };
}

/* ---------- Renderização de slide no canvas ---------- */

export function drawSlide(
  ctx: CanvasRenderingContext2D,
  w: number,
  h: number,
  slide: SlideDef,
  progress: number,
) {
  const grad = ctx.createRadialGradient(w / 2, h / 2, 50, w / 2, h / 2, Math.max(w, h) / 1.2);
  grad.addColorStop(0, "#1a1408");
  grad.addColorStop(1, "#000000");
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, w, h);

  const bannerH = Math.round(h * 0.11);
  const bgrad = ctx.createLinearGradient(0, 0, w, 0);
  bgrad.addColorStop(0, "#eab308");
  bgrad.addColorStop(1, "#f59e0b");
  ctx.fillStyle = bgrad;
  ctx.fillRect(0, 0, w, bannerH);
  ctx.fillStyle = "#000";
  ctx.font = `900 ${Math.round(bannerH * 0.42)}px system-ui, -apple-system, sans-serif`;
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText("🤖 IA PALPITE GURU", w / 2, bannerH * 0.4);
  ctx.font = `600 ${Math.round(bannerH * 0.22)}px system-ui, sans-serif`;
  ctx.fillText("Inteligência Artificial do Jogo do Bicho", w / 2, bannerH * 0.75);

  const easeIn = Math.min(1, progress * 3);
  ctx.globalAlpha = easeIn;
  ctx.fillStyle = "#facc15";
  ctx.font = `900 ${Math.round(h * 0.055)}px system-ui, sans-serif`;
  ctx.textAlign = "center";
  ctx.fillText(slide.titulo, w / 2, bannerH + h * 0.08);

  const startY = bannerH + h * 0.18;
  const lineGap = h * 0.09;
  slide.linhas.forEach((linha, idx) => {
    const linhaProgress = Math.min(1, Math.max(0, progress * slide.linhas.length - idx));
    ctx.globalAlpha = linhaProgress;
    const scale = 0.85 + 0.15 * linhaProgress;
    ctx.save();
    ctx.translate(w / 2, startY + idx * lineGap);
    ctx.scale(scale, scale);
    ctx.fillStyle = "#fde68a";
    const isNumeric = /^\d[\d\s\-\/•·↔]*\d$/.test(linha.trim());
    const size = isNumeric ? h * 0.075 : h * 0.045;
    ctx.font = `900 ${Math.round(size)}px system-ui, sans-serif`;
    ctx.shadowColor = "rgba(250,204,21,0.6)";
    ctx.shadowBlur = 20;
    ctx.fillText(linha, 0, 0);
    ctx.shadowBlur = 0;
    ctx.restore();
  });

  if (slide.destaque) {
    ctx.globalAlpha = easeIn;
    ctx.font = `${Math.round(h * 0.12)}px sans-serif`;
    ctx.fillText(slide.destaque, w / 2, h - h * 0.08);
  }
  ctx.globalAlpha = 1;
}

/* ---------- Renderer genérico ---------- */

export async function gerarVideoDeSlides(
  slides: SlideDef[],
  opts: VideoOptions,
): Promise<Blob> {
  const [W, H] = opts.formato === "vertical" ? [1080, 1920] : [1920, 1080];
  const canvas = document.createElement("canvas");
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext("2d")!;

  const durations = computeDurations(slides, opts);
  const cumulative: number[] = [];
  durations.reduce((acc, d, i) => (cumulative[i] = acc + d), 0);
  const totalSec = durations.reduce((a, b) => a + b, 0);
  const fps = 30;

  const audioCtx = new AudioContext();
  const { destination: audioDest, stop: stopAudio } = startAudioTrack(audioCtx, opts.trilha);

  const videoStream = canvas.captureStream(fps);
  const combined = new MediaStream([
    ...videoStream.getVideoTracks(),
    ...audioDest.stream.getAudioTracks(),
  ]);

  const preferred = [
    "video/webm;codecs=vp9,opus",
    "video/webm;codecs=vp8,opus",
    "video/webm",
  ];
  const mime = preferred.find((m) => MediaRecorder.isTypeSupported(m)) || "video/webm";
  const recorder = new MediaRecorder(combined, { mimeType: mime, videoBitsPerSecond: 6_000_000 });
  const chunks: Blob[] = [];
  recorder.ondataavailable = (e) => e.data.size && chunks.push(e.data);

  return new Promise((resolve, reject) => {
    recorder.onerror = (e) => reject(e);
    recorder.onstop = () => {
      stopAudio();
      audioCtx.close();
      resolve(new Blob(chunks, { type: mime }));
    };
    recorder.start();

    const startedAt = performance.now();

    const tick = () => {
      const elapsedSec = (performance.now() - startedAt) / 1000;
      if (elapsedSec >= totalSec) {
        drawSlide(ctx, W, H, slides[slides.length - 1], 1);
        recorder.stop();
        opts.onProgress?.(1);
        return;
      }
      // achar slide atual
      let slideIdx = 0;
      for (let i = 0; i < cumulative.length; i++) {
        if (elapsedSec < cumulative[i]) {
          slideIdx = i;
          break;
        }
      }
      const slideStart = slideIdx === 0 ? 0 : cumulative[slideIdx - 1];
      const p = Math.min(1, (elapsedSec - slideStart) / durations[slideIdx]);
      drawSlide(ctx, W, H, slides[slideIdx], p);
      opts.onProgress?.(Math.min(0.99, elapsedSec / totalSec));
      requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  });
}

export async function gerarVideoPalpites(
  data: PalpitesData,
  opts: VideoOptions,
): Promise<Blob> {
  const slides = buildSlides(data, opts.slides);
  return gerarVideoDeSlides(slides, opts);
}

/* ---------- Miniatura PNG ---------- */

export async function gerarMiniaturaPalpites(
  data: PalpitesData,
  formato: FormatoVideo = "vertical",
): Promise<Blob> {
  const [W, H] = formato === "vertical" ? [1080, 1920] : [1920, 1080];
  const canvas = document.createElement("canvas");
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext("2d")!;
  const slides = buildSlides(data, "todos");
  drawSlide(ctx, W, H, slides[0], 1);
  return new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (b) => (b ? resolve(b) : reject(new Error("Falha ao gerar miniatura"))),
      "image/png",
    );
  });
}

export function baixarBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 4000);
}
